import { useMemo } from 'react';

import { Amount } from '@/components/common/Amount';
import type {
  BudgetMonth,
  CategoryGroupMonth,
  CategoryMonth,
} from '@/api/types';
import { useBudgetStore } from '@/store/budget';
import { theme } from '@/style/theme';
import { makeBalanceAmountStyle, tnum } from '@/utils/styles';
import { BudgetGroupRow } from './BudgetRows';

const SUBCOLUMN = 96;

/**
 * The envelope grid: a Category column, then Budgeted / Spent / Balance for
 * every visible month. Port of `budget/BudgetTable.tsx` + `BudgetTotals.tsx`.
 */
export function BudgetTable({ months }: { months: BudgetMonth[] }) {
  const columns = `240px repeat(${months.length * 3}, minmax(${SUBCOLUMN}px, 1fr))`;

  const layout = useMemo(() => {
    const order: { id: string; name: string; isIncome: boolean }[] = [];
    const groupCells = new Map<string, CategoryGroupMonth[]>();

    months.forEach((month, monthIndex) => {
      for (const group of month.groups) {
        if (!groupCells.has(group.id)) {
          order.push({
            id: group.id,
            name: group.name,
            isIncome: group.is_income === 1,
          });
          groupCells.set(group.id, []);
        }
        const list = groupCells.get(group.id);
        if (list) list[monthIndex] = group;
      }
    });

    const categoryCells = new Map<string, CategoryMonth[]>();
    for (const month of months) {
      for (const group of month.groups) {
        for (const category of group.categories) {
          if (!categoryCells.has(category.id)) {
            categoryCells.set(category.id, []);
          }
          categoryCells.get(category.id)![months.indexOf(month)] = category;
        }
      }
    }

    // Income first, matching upstream's fixed header group.
    order.sort((a, b) => Number(b.isIncome) - Number(a.isIncome));

    return { order, groupCells, categoryCells };
  }, [months]);

  const monthLabels = ['已预算', '已支出', '结余'];
  const refreshing = useBudgetStore(state => state.loading);

  return (
    <div
      className="scrollable min-h-0 flex-1 overflow-auto border-t border-table-border bg-table-background"
      aria-busy={refreshing}
      data-testid="budget-table"
    >
      <div style={{ minWidth: `calc(240px + ${months.length * 3 * SUBCOLUMN}px)` }}>
        <div
          className="sticky top-0 z-10 grid border-b border-table-border bg-table-header-background"
          style={{ gridTemplateColumns: columns }}
        >
          <div className="px-2.5 py-2 text-sm text-table-header-text">
            分类
          </div>
          {months.map(month =>
            monthLabels.map(label => (
              <div
                key={`${month.month}-${label}`}
                className="px-2 py-2 text-center text-sm text-table-header-text"
              >
                {label}
              </div>
            )),
          )}
        </div>

        {/* Month totals, directly under the header like the demo grid. */}
        <div
          className="grid border-b border-table-border bg-table-header-background text-sm font-semibold"
          style={{ gridTemplateColumns: columns }}
          data-testid="budget-totals"
        >
          <div className="px-2.5 py-1.5" style={{ color: theme.tableHeaderText }} />
          {months.map(month => (
            <div key={month.month} className="contents">
              <div className="px-2 py-1.5 text-right" style={tnum}>
                <Amount value={month.totals.budgeted} />
              </div>
              <div className="px-2 py-1.5 text-right" style={tnum}>
                <Amount value={month.totals.spent} greyZero />
              </div>
              <div
                className="px-2 py-1.5 text-right"
                style={{ ...tnum, ...makeBalanceAmountStyle(month.totals.balance) }}
              >
                <Amount value={month.totals.balance} />
              </div>
            </div>
          ))}
        </div>

        <div>
          {layout.order.map(group => (
            <BudgetGroupRow
              key={group.id}
              columns={columns}
              months={months.map(month => month.month)}
              groupId={group.id}
              name={group.name}
              isIncome={group.isIncome}
              cells={layout.groupCells.get(group.id) ?? []}
              categoryCells={layout.categoryCells}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
